/**
 * ULD航班状态统计表格组件
 * 专门用于展示当前位于航班上的ULD统计信息
 */

import React, { useState, useEffect } from 'react';
import {
    Table,
    Space,
    Typography,
    Tag,
    Tooltip,
    Alert,
    Badge,
    Empty,
    Button,
    Spin,
} from 'antd';
import {
    RocketOutlined,
    ClockCircleOutlined,
    WarningOutlined,
    InfoCircleOutlined,
    ReloadOutlined,
    DownOutlined,
    RightOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { uldService } from '../../../../api/enhancedApi';
import { ULD_CORE_STATUS_COLORS, ULD_CORE_STATUS_LABELS } from '../constants/statisticsConstants';

dayjs.extend(relativeTime);

const { Title, Text } = Typography;

/**
 * ULD航班统计表格组件
 */
const UldFlightTable = ({ refreshTrigger }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [flightData, setFlightData] = useState([]);
    const [summaryData, setSummaryData] = useState(null);

    const fetchFlightStatistics = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await uldService.getFlightUldStatistics({});

            const apiData = response.data;
            if (apiData.success && apiData.data) {
                const { summary, flight_details } = apiData.data;
                setSummaryData(summary);
                setFlightData(transformFlightData(flight_details || []));
            } else if (apiData.summary || apiData.flight_details) {
                setSummaryData(apiData.summary);
                setFlightData(transformFlightData(apiData.flight_details || []));
            } else {
                setFlightData([]);
                setSummaryData(null);
            }
        } catch (fetchError) {
            setError('获取航班统计失败，请稍后重试');
            setFlightData([]);
            setSummaryData(null);
        } finally {
            setLoading(false);
        }
    };

    // 转换航班数据为表格格式
    const transformFlightData = (flightDetails) => {
        return flightDetails.map((flight, index) => ({
            key: flight.flight_identifier || index,
            flightNumber: flight.flight_number,
            flightType: flight.flight_type,
            departureAirport: flight.departure_airport,
            arrivalAirport: flight.arrival_airport,
            uldCount: flight.uld_count,
            uldTypes: flight.uld_types,
            uldList: flight.uld_list || [],
            std: flight.std,
            sta: flight.sta,
            children: (flight.uld_list || []).map((uld, uldIndex) => ({
                key: `${flight.flight_identifier}-${uldIndex}`,
                isChild: true,
                uldNumber: uld.uld_number,
                uldType: uld.type,
                coreStatus: uld.core_status,
                availability: uld.availability
            }))
        }));
    };

    // 表格列定义
    const columns = [
        {
            title: '航班号',
            dataIndex: 'flightNumber',
            key: 'flightNumber',
            width: 180,  // 调整宽度适应航班号/日期格式
            render: (flightNumber, record) => {
                if (record.isChild) {
                    return (
                        <div style={{ paddingLeft: 24 }}>
                            <Text strong style={{ color: '#1890ff' }}>{record.uldNumber}</Text>
                            <Text type="secondary" style={{ fontSize: '12px', marginLeft: 8 }}>
                                ({record.uldType})
                            </Text>
                        </div>
                    );
                }
                
                // 格式化航班号显示：航班号/起飞日期（YYYYMMDD）
                const formatFlightDisplay = () => {
                    if (!flightNumber) return '—';
                    
                    // 如果有STD（计划起飞时间），提取日期
                    if (record.std) {
                        const flightDate = dayjs(record.std).format('YYYYMMDD');
                        return `${flightNumber}/${flightDate}`;
                    }
                    
                    // 如果没有STD，只显示航班号
                    return flightNumber;
                };
                
                return (
                    <Space>
                        <Text strong>{formatFlightDisplay()}</Text>
                        {record.flightType === 'external' && (
                            <Tag color="orange" size="small">外部</Tag>
                        )}
                        {record.flightType === 'system' && (
                            <Tag color="green" size="small">系统</Tag>
                        )}
                    </Space>
                );
            },
        },
        {
            title: '航线',
            key: 'route',
            width: 100,  // 减小宽度
            render: (_, record) => {
                if (record.isChild) return null;
                
                const departure = record.departureAirport || '—';
                const arrival = record.arrivalAirport || '—';
                return (
                    <Text type="secondary">
                        {departure} → {arrival}
                    </Text>
                );
            },
        },
        {
            title: 'ULD数量',
            dataIndex: 'uldCount',
            key: 'uldCount',
            width: 90,  // 调整宽度
            align: 'center',
            render: (count, record) => {
                if (record.isChild) {
                    // 子行显示状态 - 使用中文标签
                    const statusColor = ULD_CORE_STATUS_COLORS[record.coreStatus] || 'default';
                    const statusLabel = ULD_CORE_STATUS_LABELS[record.coreStatus] || record.coreStatus || '未知';
                    return (
                        <Tag color={statusColor} size="small">
                            {statusLabel}
                        </Tag>
                    );
                }
                return <Text strong>{count}</Text>;
            },
        },
        {
            title: 'ULD类型分布',
            key: 'uldTypes',
            width: 200,  // 保持足够宽度显示多个标签
            render: (_, record) => {
                if (record.isChild) return null;
                
                if (!record.uldTypes || Object.keys(record.uldTypes).length === 0) {
                    return <Text type="secondary">无数据</Text>;
                }
                
                return (
                    <Space wrap>
                        {Object.entries(record.uldTypes).map(([type, count]) => (
                            <Tag key={type} color="blue" size="small">
                                {type}: {count}
                            </Tag>
                        ))}
                    </Space>
                );
            },
        },
    ];

    // 初始化和依赖更新
    useEffect(() => {
        fetchFlightStatistics();
    }, [refreshTrigger]);

    return (
        <div>
            {error && (
                <Alert
                    message="航班统计加载失败"
                    description={error}
                    type="error"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            )}
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Title level={4} style={{ margin: 0 }}>
                    航班维度分析
                </Title>
                <Button 
                    type="default" 
                    icon={<ReloadOutlined />}
                    onClick={fetchFlightStatistics}
                    loading={loading}
                    size="small"
                >
                    刷新数据
                </Button>
            </div>

            {/* 简单的汇总信息 */}
            {summaryData && (
                <Alert
                    message={
                        <Space>
                            <Text>
                                <Text strong>{summaryData.total_flights}</Text> 个航班，
                                共有 <Text strong>{summaryData.total_flight_ulds}</Text> 个ULD在运输中
                            </Text>
                        </Space>
                    }
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            )}

            {/* 航班列表表格 */}
            <Spin spinning={loading}>
                {flightData && flightData.length > 0 ? (
                    <Table
                        columns={columns}
                        dataSource={flightData}
                        size="small"
                        pagination={{
                            total: flightData.length,
                            pageSize: 10,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total, range) =>
                                `第 ${range[0]}-${range[1]} 条，共 ${total} 个航班`,
                        }}
                        expandable={{
                            expandIcon: ({ expanded, onExpand, record }) => {
                                // 如果是ULD子行，不显示展开图标
                                if (record.isChild || !record.children || record.children.length === 0) {
                                    return null;
                                }
                                
                                return (
                                    <span 
                                        onClick={e => onExpand(record, e)}
                                        style={{ cursor: 'pointer', marginRight: 8 }}
                                    >
                                        {expanded ? <DownOutlined /> : <RightOutlined />}
                                    </span>
                                );
                            },
                            childrenColumnName: 'children',
                            indentSize: 0,
                            // 确保ULD子行不可点击展开
                            expandRowByClick: false,
                        }}
                        scroll={{ x: 570 }}
                    />
                ) : (
                    <Empty 
                        description={
                            loading ? '正在加载数据...' : (error ? '加载失败' : '暂无航班ULD数据')
                        }
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                )}
            </Spin>
        </div>
    );
};

export default UldFlightTable; 